import React from 'react';
import autobind from 'autobind';
import { observer, inject } from 'mobx-react';

import '@components/Print/Print_.scss';

import { AppState } from '@store/App/App';
import { AuthUser } from '@store/AuthUser/AuthUser';
import { Ehr } from '@patient/store/ehr/Ehr';
import { EHR_CONTENT_ID } from '../../pages/ProfilePage/ProfilePage';
import { RightContent, RightContentParams } from './Right';

import { Print } from '@components/Print';
import { withRouter, WithRouterProps } from '@hoc/withRouterProps';
import { ehrService } from '@patient/services/ehr/ehrService';
import { translate } from '@chaika/i18n/helper';
import { assertIsDefined } from '@utils/assertIsDefined';

interface Props extends RightContentParams {
    patientId: string | number;
    onReplenishPersonalAccountClick(): void;
}

interface StoreProps {
    ehr: Ehr;
    app: AppState;
    authUser: AuthUser;
}

const injectableStores: (keyof StoreProps)[] = [Ehr.Name, AuthUser.Name, AppState.Name];

const EHR_CONTENT_MESSAGE = 'EHR print is started from the header only while the EHR content is rendered';

@(withRouter as any)
@observer
class Component extends React.Component<Props & StoreProps & WithRouterProps> {
    public render(): React.ReactNode {
        const { authUser, showReplenishButton, showPrintButton, showPrintTitleListButton } = this.props;

        return React.createElement(RightContent, {
            showReplenishButton,
            showPrintButton,
            showPrintTitleListButton,
            hoverText: !authUser.organizationId ? translate('selectOrganizationToReceive') : '',
            onReplenishClick: this.handleReplenishClick,
            onPrintClick: this.handlePrint,
            onPrintTitleListClick: this.handlePrintTitleList,
            onRemoveNotExistEntriesClick: authUser.hasRootAccess ? this.onRemoveNotExistEntriesClick : undefined,
        });
    }

    @autobind
    private handleReplenishClick(): void {
        const { app, authUser, onReplenishPersonalAccountClick } = this.props;

        if (!authUser.organizationId) {
            app.setSelectOrganizationPopupParams({});
        } else {
            onReplenishPersonalAccountClick();
        }
    }

    @autobind
    private async handlePrint() {
        Print.printContent({
            contentId: EHR_CONTENT_ID,
            onLoadingStart: this.addPrintClass,
            onLoadingEnd: this.removePrintClass,
        });
    }

    @autobind
    private async onRemoveNotExistEntriesClick() {
        await ehrService.deleteNotExistsEntryFromElasticCommand(this.props.ehr.id);
    }

    @autobind
    private async handlePrintTitleList() {
        const { location } = this.props;
        window.open(`${location.pathname}/title-list`, '_blank');
    }

    @autobind
    private addPrintClass(): void {
        const el = document.getElementById(EHR_CONTENT_ID);
        assertIsDefined(el, EHR_CONTENT_MESSAGE);
        el.classList.add('print_root');
    }

    @autobind
    private removePrintClass(): void {
        const el = document.getElementById(EHR_CONTENT_ID);
        assertIsDefined(el, EHR_CONTENT_MESSAGE);
        el.classList.remove('print_root');
    }
}

export const RightContentContainer = inject<Props, StoreProps>(...injectableStores)(Component);
